<template>
  <teleport v-if="visible && !disabled" :to="step.parent || 'body'">
    <transition name="guide-fade">
      <div v-if="visible" class="guide-body">
        <!-- mask蒙层 -->
        <div
          v-if="open"
          ref="highlightLayerRef"
          :class="highlightMaskCls"
          :style="{ zIndex: computedZIndex + 1, ...highlightStyle }"
        />

        <!-- step面板 -->
        <jd-tooltip
          ref="popoverRef"
          :visible="open"
          :placement="step.placement || 'auto'"
          :popper-class="`
          ${ns.e('panel')}
          ${ns.is(type)}
          ${ns.is(step?.color || color ? 'colorful' : '')}
          ${panelClass}
          `"
          :popper-style="{
            '--guide-bg-color': step?.color || color,
          }"
          :offset="step.offset || 12"
          :z-index="computedZIndex + 2"
          :effect="effect"
        >
          <div
            v-if="!hideHint"
            :class="ns.e('trigger-hint')"
            :style="{ zIndex: computedZIndex + 2, ...highlightStyle }"
            @click="handleOpen"
          />
          <template #content>
            <div :class="ns.e('inner')">
              <div v-if="$slots.extra" :class="ns.e('extra')">
                <slot name="extra" />
              </div>

              <div
                v-if="closable"
                :class="ns.e('close-icon')"
                @click="handleClose"
              >
                <Close />
              </div>

              <!-- 面板封面 -->
              <div
                v-if="step.cover || $slots.cover"
                :class="`${ns.e('cover')} ${
                  closable ? ns.e('cover-with-close') : ''
                }`"
              >
                <slot v-if="$slots.cover" name="cover" />
                <component
                  :is="step.cover?.type"
                  v-else
                  v-bind="step.cover?.props"
                />
              </div>

              <!-- 面板标题 -->
              <div v-if="step.title || $slots.title" :class="ns.e('title')">
                <slot v-if="$slots.title" name="title" />
                <template v-else-if="typeof step.title === 'string'">
                  {{ step.title }}
                </template>
              </div>

              <!-- 面板内容 -->
              <div
                v-if="step.description || $slots.desc"
                :class="ns.e('description')"
              >
                <slot v-if="$slots.desc" name="desc" />
                <template v-else-if="typeof step.description === 'string'">
                  {{ step.description }}
                </template>
                <component :is="step.description" v-else />
              </div>

              <!-- 面板footer -->
              <div :class="ns.e('footer')">
                <div />
                <!-- 按钮 -->
                <div class="buttons">
                  <jd-button 
                    type="primary" size="small" 
                    @click="handleFinish" 
                    class="finish"
                    :ignore-form-disabled="ignoreFormDisabled"
                  >
                    {{ t('el.guide.finishNoStep') }}
                  </jd-button>
                </div>
              </div>
            </div>
          </template>
        </jd-tooltip>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from 'vue'
import { isNumber } from '@vueuse/core'
import { useFormDisabled } from '@jdesign-vue/components/form'
import { useLocale, useNamespace, useZIndex } from '@jdesign-vue/hooks'
import JdTooltip from '@jdesign-vue/components/tooltip'
import { Close } from '@jd/jdesign-icons-vue'
import { isEqual } from 'lodash-unified'
import { calcHLStyle } from './utils'

import { guideHintEmits, guideHintProps } from './guide-hint'
import type { RectType } from './guide'

defineOptions({
  name: 'JdGuideHint',
})

const disabled = useFormDisabled()

type TargetType = keyof HTMLElementTagNameMap | (() => HTMLElement) | undefined

const props = defineProps(guideHintProps)
const emits = defineEmits(guideHintEmits)

const { mask } = toRefs(props)

const highlightMaskCls = computed(() =>
  mask.value ? ns.e('highlight-mask') : ns.e('highlight-no-mask')
)
const { t } = useLocale()
const ns = useNamespace('guide')
const { nextZIndex } = useZIndex()
const defaultZIndex = nextZIndex()
const computedZIndex = computed(() =>
  isNumber(props?.zIndex) ? props?.zIndex : defaultZIndex
)

const cacheDomRect = ref<RectType>()

// 高亮层，用于高亮元素
const highlightLayerRef = ref()
const popoverRef = ref()
const hideHint = ref(true)

const open = ref(props.open)

const highlightStyle = ref()

let animationFrameId = 0

const findTarget = (target: TargetType) => {
  if (!target) {
    hideHint.value = true
    return
  }
  let realDom
  if (typeof target === 'function') {
    realDom = target()
  } else {
    realDom = document.querySelector(target) as HTMLElement
  }
  hideHint.value = false
  return realDom
}

const updateHLStyle = (realDom: HTMLElement) => {
  highlightStyle.value = calcHLStyle({
    realDom,
    type: 'hint',
    offset: props.offset,
    cacheDomRect,
    parent: props.step.parent,
  })
}

const handleOpen = (e: MouseEvent) => {
  e?.stopPropagation()
  e?.preventDefault()
  open.value = true
  emits('open')
}
const handleClose = () => {
  open.value = false
  emits('close')
}
//对外暴露关闭指引框方法
function tooltipClose() {
  handleClose()
}
const handleFinish = () => {
  emits('finish')
  handleClose()
}

watch(
  () => props.step?.target,
  (newTarget: any) => {
    updateStyleByAnimation(newTarget)
  }
)

const updateStyleByAnimation = (target: TargetType) => {
  if (animationFrameId) cancelAnimationFrame(animationFrameId)
  const animate = () => {
    const realDom = findTarget(target)
    if (!realDom) {
      animationFrameId = requestAnimationFrame(animate)
      return
    }
    const { top, width, left, height } = realDom.getBoundingClientRect()
    const compareObj = { top, width, left, height }
    const equal = isEqual(compareObj, cacheDomRect.value)
    if (equal) {
      animationFrameId = requestAnimationFrame(animate)
      return
    }
    updateHLStyle(realDom)
    props.autoReposition && popoverRef.value?.updatePopper?.()
    // 下一帧
    animationFrameId = requestAnimationFrame(animate)
  }

  // 启动
  animationFrameId = requestAnimationFrame(animate)
}
onMounted(() => {
  const { target } = props.step
  updateStyleByAnimation(target)
})
defineExpose({ tooltipClose })
onUnmounted(() => {
  cancelAnimationFrame(animationFrameId)
})
</script>
